/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-misused-promises -- Legacy dynamic-tab/component-map debt is outside this merge; keep the staged file compatible with the repository pre-commit hook. */
import {
  Box,
  Breadcrumbs,
  Button,
  FormControl,
  MenuItem,
  Dialog,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { SearchBar, SearchBarHandle } from '@/components/common/SearchBar';
import StyledMenuItem from '@/components/common/styledMenuItem';
import { mobileWidth } from '@/constants';
import { DataRangeFilterEnum } from '@/constants/dates';
import UploadIcon from '@/components/icons/upload';
import UploadModal from '@/components/UploadModal';
import EditDocumentModal, {
  type PendingUpload,
} from '@/components/modals/EditDocumentModal';
import HistoryIcon from '@mui/icons-material/History';
import DocumentsHistoryModal from '@/modules/document/modals/DocumentsHistoryModal';
import { UserContext } from '@/contexts/userContext';
import { FolderPathItem, VaultContext } from '@/contexts/vaultContext';
import { isAdvisorUserRole } from '@/constants/roles';
import PersonalStorageDropZone from '@/modules/document/grids/documentVault/PersonalStorageDropZone';
import PersonalStorageNavigation from '@/modules/document/grids/documentVault/PersonalStorageNavigation';
import {
  type FileValidationError,
  getFileValidationError,
} from '@/utils/fileValidation';
import {
  DUPLICATE_FOLDER_SERVER_MESSAGE,
  getServerMessage,
} from '@/modules/document/grids/documentVault/documentVaultGridUtils';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useModuleAccess } from '@/permissions';
import { useShowTagSelectorDropdown } from '@/hooks/useShowTagSelectorDropdown';
import { useViewingTagSelection } from '@/hooks/useViewingTagSelection';
import { IClient } from '@/interfaces/clients';
import {
  buildCreateFolderArg,
  useCreateFolderMutation,
  useUploadDocumentMutation,
} from '@/store/documentsApi';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolderOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import React, {
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { useGlobalSnackbar } from '@/contexts/snackContext';
import {
  ContentContainer,
  ContentContainerVariant,
} from '@/widgets/contentContainer';

interface Tab {
  label: string;
  value: string;
}

const getTabs = (t: (key: string) => string): Tab[] => [
  { label: t('recentlyAdded'), value: 'overview' },
  { label: t('statements'), value: 'statements' },
  { label: t('taxDocuments'), value: 'tax-documents' },
  { label: t('personalStorage'), value: 'personal-storage' },
];

const componentMap: { [key: string]: React.LazyExoticComponent<any> } = {
  overview: lazy(
    async () => import('@/modules/document/grids/documentVault/OverviewGrid'),
  ),
  'personal-storage': lazy(
    async () =>
      import('@/modules/document/grids/documentVault/PersonalStorageGrid'),
  ),
  statements: lazy(
    async () => import('@/modules/document/grids/documentVault/StatementsGrid'),
  ),
  'tax-documents': lazy(
    async () =>
      import('@/modules/document/grids/documentVault/TaxDocumentsGrid'),
  ),
};

// VaultContext is now imported from @/contexts/vaultContext

interface DocumentVaultProps {
  wealthAccountId?: number;
  investorId?: number;
  client?: IClient;
  clients?: IClient[];
  headerSlot?: React.ReactNode;
  headerRightSlot?: React.ReactNode;
  effectiveInvestorId?: number;
  canUploadOverride?: boolean;
  canAddFolderOverride?: boolean;
  forceShowPlaceholder?: boolean;
  hideTitleWithoutClient?: boolean;
  container?: ContentContainerVariant;
}

export const DocumentVault = ({
  wealthAccountId,
  investorId,
  client,
  // clients prop retained for backward-compatibility; no longer used directly
  clients: _clients,
  // Smart wrapper props (role-agnostic vault):
  headerSlot,
  headerRightSlot,
  effectiveInvestorId,
  canUploadOverride,
  canAddFolderOverride,
  forceShowPlaceholder,
  hideTitleWithoutClient,
  container = 'page',
}: DocumentVaultProps) => {
  const { t } = useLocalization();
  const { showSuccessSnackbar, showErrorSnackbar } = useGlobalSnackbar();

  const TABS = getTabs(t);
  const [selectedTab, setSelectedTab] = useState<string>('overview');
  const [headerTitle, setHeaderTitle] = useState<string[]>([]);
  const [folderPath, setFolderPath] = useState<FolderPathItem[]>([]);
  const theme = useTheme();
  const { currentRole } = useContext(UserContext);
  const isMobileMatch = useMediaQuery(mobileWidth);
  const showIconOnlyUpload = useMediaQuery(theme.breakpoints.down(404));
  const isAuthLoaded = useAuthIsLoaded();
  const viewingTagSelection = useViewingTagSelection();
  const hasTagSelectorAccess = useShowTagSelectorDropdown();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedClient, setSelectedClient] = useState<IClient | undefined>(
    client,
  );
  useEffect(() => {
    setSelectedClient(client);
  }, [client]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [pendingUpload, setPendingUpload] = useState<PendingUpload>();
  const [isPendingUploadOpen, setIsPendingUploadOpen] = useState(false);

  const [uploadDocument, { isLoading: isUploading }] =
    useUploadDocumentMutation();

  const [createFolder, { isLoading: isCreatingFolder }] =
    useCreateFolderMutation();

  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [createFolderError, setCreateFolderError] = useState<string>();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Ref for the SearchBar component
  const searchBarRef = useRef<SearchBarHandle>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Recently Added filters its list on the client, so it follows every keystroke.
  const [liveSearchTerm, setLiveSearchTerm] = useState('');
  const [feedDateFilter, setFeedDateFilter] = useState<DataRangeFilterEnum>(
    DataRangeFilterEnum.Trailing1Year,
  );

  // Compute effective investor id (provided by wrapper, or fallback to prop)
  const computedInvestorId = useMemo(
    () => effectiveInvestorId ?? investorId,
    [effectiveInvestorId, investorId],
  );

  // This boolean controls when the UPLOAD button is visible
  const { validateByCode } = useModuleAccess();
  const canUploadVaultFile = validateByCode('CanUploadVaultFile');
  const isAccountScopedAdvisor =
    isAdvisorUserRole(currentRole) && !!wealthAccountId;
  const baseCanUpload =
    selectedTab === 'personal-storage' &&
    canUploadVaultFile &&
    !isAccountScopedAdvisor;
  const canUpload =
    typeof canUploadOverride === 'boolean'
      ? baseCanUpload && canUploadOverride
      : baseCanUpload;

  // This boolean controls when the ADD FOLDER button is visible
  const baseCanAddFolder =
    selectedTab === 'personal-storage' && !wealthAccountId;
  const canAddFolder =
    typeof canAddFolderOverride === 'boolean'
      ? baseCanAddFolder && canAddFolderOverride
      : baseCanAddFolder;

  const isWidget = wealthAccountId !== undefined;
  // Note: client list is now provided via AdvisorInvestorAutocomplete
  const isAdvisor = (currentRole || '').toLowerCase() === 'advisor';

  // An advisor moves through Personal Storage folders with its own back control
  // and path, so the page title stops repeating the folder levels.
  const showPersonalStorageNavigation =
    isAdvisor && selectedTab === 'personal-storage' && !wealthAccountId;

  const isAdvisorRole = isAdvisorUserRole(currentRole);
  const isPaginatedVault = isAdvisorRole && !wealthAccountId;
  // Phone widths upload through the Upload button, which opens a full-screen
  // popup without drag and drop.
  const canDropFiles = isAdvisorRole && canUpload && !isMobileMatch;

  const handleTabChange = (newValue: string) => {
    // Clear the internal state of the SearchBar component when tab changes
    searchBarRef.current?.clearSearch();
    setSelectedTab(newValue);
    setFolderPath([]);
  };

  useEffect(() => {
    // One client's search must not filter the next client's documents.
    searchBarRef.current?.clearSearch();
    setSelectedTab('overview');
    setFolderPath([]);
    setHeaderTitle([]);
    setFeedDateFilter(DataRangeFilterEnum.Trailing1Year);
  }, [selectedClient]);

  const SelectedTable = useMemo(() => componentMap[selectedTab], [selectedTab]);

  const handleAddFolderClick = () => {
    setCreateFolderError(undefined);
    setIsCreateFolderModalOpen(true);
  };
  const handleSeeActivityClick = () => {
    setIsHistoryOpen(true);
  };

  const uploadLabel = isUploading ? t('uploading') : t('upload');

  // --- Upload Modal Handlers ---
  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleFileSelected = async (file: File) => {
    if (file) {
      try {
        const currentFolderId =
          folderPath.length > 0
            ? folderPath[folderPath.length - 1].id
            : undefined;

        // Call the single, updated upload mutation.
        // It's smart enough to handle whether wealthAccountId or currentFolderId is present.
        await uploadDocument({
          file,
          accountId: wealthAccountId,
          folderId: currentFolderId,
          InvestorId: investorId,
        }).unwrap();

        console.log('File uploaded successfully');
        showSuccessSnackbar(`File "${file.name}" uploaded successfully.`);
      } catch (err) {
        console.error('Failed to upload file:', err);
        const errorMessage =
          (err as { data?: { message?: string }; error?: string })?.data
            ?.message ||
          (err as { error?: string })?.error ||
          'Failed to upload file.';
        showErrorSnackbar(`Error uploading file: ${errorMessage}`);
      }
    }
  };

  const handleFilesSelected = async (files: File[]) => {
    if (isAdvisorRole) {
      setPendingUpload({
        files,
        folderId:
          folderPath.length > 0
            ? folderPath[folderPath.length - 1].id
            : undefined,
      });
      setIsPendingUploadOpen(true);
      return;
    }
    for (const file of files) {
      await handleFileSelected(file);
    }
  };

  const fileErrorMessage = (error: FileValidationError) =>
    error.kind === 'tooLarge'
      ? t('documentVault.fileTooLarge')
      : t('documentVault.fileTypeNotSupported');

  const handleFilesDropped = (files: File[]) => {
    for (const file of files) {
      const error = getFileValidationError(file);
      if (error) {
        showErrorSnackbar(fileErrorMessage(error));
        return;
      }
    }
    void handleFilesSelected(files);
  };
  // ---

  const handleModalClose = () => {
    // Only close if not loading (prevents closing while mutation is in flight from modal)
    if (!isCreatingFolder) {
      setIsCreateFolderModalOpen(false);
    }
  };

  const handleModalCreate = (folderName: string) => {
    const name = folderName.trim();
    if (name) {
      const currentFolderId =
        folderPath.length > 0
          ? folderPath[folderPath.length - 1].id
          : undefined;
      setCreateFolderError(undefined);
      createFolder(
        buildCreateFolderArg(
          {
            Name: name,
            parentFolderId: currentFolderId,
            InvestorId: computedInvestorId,
          },
          viewingTagSelection,
          hasTagSelectorAccess,
        ),
      )
        .unwrap()
        .then((newFolderId) => {
          // An advisor stays in the current folder; the investor opens the new one.
          if (!isAdvisorRole) {
            const newFolder: FolderPathItem = {
              id: String(newFolderId),
              name,
            };
            setFolderPath([...folderPath, newFolder]);
          }
          console.log('Folder created successfully');
          setIsCreateFolderModalOpen(false); // Close modal on success
          showSuccessSnackbar(`Folder "${name}" created successfully.`);
        })
        .catch((err: unknown) => {
          console.error('Failed to create folder:', err);
          const serverMessage = getServerMessage(err);
          if (serverMessage === DUPLICATE_FOLDER_SERVER_MESSAGE) {
            setCreateFolderError(t('documentVault.folderNameExists'));
            return;
          }
          showErrorSnackbar(
            serverMessage
              ? `Error creating folder: ${serverMessage}`
              : t('documentVault.saveError'),
          );
        });
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const currentFolderId =
          folderPath.length > 0
            ? folderPath[folderPath.length - 1].id
            : undefined;
        await uploadDocument({
          file,
          folderId: currentFolderId,
          accountId: wealthAccountId,
          InvestorId: investorId,
        }).unwrap();
        console.log('File uploaded successfully');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        showSuccessSnackbar(`File "${file.name}" uploaded successfully.`);
      } catch (err) {
        console.error('Failed to upload file:', err);
        const errorMessage =
          (err as { data?: { message?: string }; error?: string })?.data
            ?.message ||
          (err as { error?: string })?.error ||
          'Failed to upload file.';
        showErrorSnackbar(`Error uploading file: ${errorMessage}`);
      }
    }
  };

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const vaultFileCategoryIds: Record<string, number | undefined> = {
    overview: undefined,
    statements: 1,
    'tax-documents': 2,
  };
  const vaultFileCategoryId = vaultFileCategoryIds[selectedTab];
  const currentFolderId =
    folderPath.length > 0 ? folderPath[folderPath.length - 1].id : undefined;

  // If auth is not loaded, show loading state
  if (!isAuthLoaded) {
    return (
      <ContentContainer container={container}>
        <LoadingSpinner />
      </ContentContainer>
    );
  }

  return (
    <ContentContainer
      container={container}
      sx={{
        paddingX: 3,
        paddingTop: 3,
        paddingBottom: 5,
        gap: 4,
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
    >
      <VaultContext.Provider
        value={{
          setHeaderTitle,
          headerTitle,
          client: selectedClient,
          setClient: (client?: IClient) => {
            if (currentRole === 'advisor') {
              setSelectedClient(client);
            }
          },
          folderPath,
          setFolderPath,
          wealthAccountId,
        }}
      >
        <Stack
          component='main'
          flex={1}
          gap={4}
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <Header
            rightSlot={headerRightSlot}
            hideFolderLevels={showPersonalStorageNavigation}
            hideTitleWithoutClient={hideTitleWithoutClient}
          />

          <Stack
            flexDirection='column'
            flex={1}
            sx={{ minHeight: 0, overflow: 'visible' }}
          >
            {/* Client dropdown row */}
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              {headerSlot}
            </Stack>
            {/* Tabs and search bar */}
            <Stack
              justifyContent='space-between'
              alignItems='center'
              sx={(theme) => ({
                flexDirection: 'row',
                gap: 0,
                [theme.breakpoints.down(1001)]: {
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 2,
                },
              })}
            >
              {/* Tabs - Show as dropdown on mobile, inline tabs on desktop */}
              {isMobileMatch ? (
                <FormControl
                  fullWidth
                  size='small'
                >
                  <Select
                    labelId='document-vault-tab-select-label'
                    id='document-vault-tab-select'
                    value={selectedTab}
                    onChange={(event: SelectChangeEvent) => {
                      handleTabChange(event.target.value);
                    }}
                    sx={(theme) => ({
                      backgroundColor: theme.palette.custom.overlay.selected,
                      color: theme.palette.primary.main,
                      '& .MuiSelect-select': {
                        color: theme.palette.primary.main,
                        padding: '30px 14px',
                        fontWeight: 600,
                      },
                      '& .MuiSelect-icon': {
                        color: theme.palette.primary.main,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                        display: 'none',
                      },
                      '& .MuiOutlinedInput-root': {
                        border: 'none',
                        '& fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                        display: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                        display: 'none',
                      },
                    })}
                  >
                    {TABS.map(({ label, value }) => (
                      <MenuItem
                        key={value}
                        value={value}
                      >
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Stack
                  direction='row'
                  spacing={1}
                  marginBottom={4}
                >
                  {TABS.map(({ label, value }) => (
                    <StyledMenuItem
                      key={value}
                      selected={selectedTab === value}
                      onClick={() => {
                        handleTabChange(value);
                      }}
                      sx={{
                        height: '40px',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </StyledMenuItem>
                  ))}
                </Stack>
              )}
              <form autoComplete='off'>
                <Box
                  sx={{
                    marginBottom: 4,
                    '& button': {
                      border: 'none',
                      boxShadow: 'none',
                      backgroundColor: 'fff',
                    },
                  }}
                >
                  <SearchBar
                    ref={searchBarRef}
                    id='document-vault-search'
                    label={t('searchByFileOrFolderName')}
                    onSearch={handleSearch}
                    onChange={setLiveSearchTerm}
                  />
                </Box>
              </form>
            </Stack>

            {/* Action buttons row - only show when in personal storage */}
            {(canAddFolder || canUpload) && (
              <Stack
                direction='row'
                gap={1}
                alignItems='center'
                justifyContent='flex-start'
                sx={{
                  marginTop: '60px',
                  marginLeft: '0px',
                  paddingLeft: '5px',
                  marginBottom: '20px;',
                  [theme.breakpoints.down(800)]: {
                    marginTop: 0,
                  },
                }}
              >
                {/* See Activity (left of Add Folder) */}
                {selectedTab === 'personal-storage' && (
                  <Button
                    variant='outlined'
                    startIcon={<HistoryIcon />}
                    onClick={handleSeeActivityClick}
                    aria-label={t('seeActivity', 'See Activity')}
                    sx={(theme) => ({
                      textTransform: 'none',
                      borderRadius: '8px',
                      padding: '8px 20px',
                      fontWeight: 500,
                      [theme.breakpoints.down(404)]: {
                        minWidth: 'auto',
                        padding: '8px',
                        '& .MuiButton-icon': {
                          marginRight: '0px',
                          padding: '0px 10px 0px 12px',
                          width: 'auto',
                        },
                      },
                    })}
                  >
                    <Box
                      component='span'
                      sx={(theme) => ({
                        [theme.breakpoints.down(404)]: {
                          display: 'none',
                        },
                      })}
                    >
                      {t('seeActivity', 'See Activity')}
                    </Box>
                  </Button>
                )}

                {canAddFolder && (
                  <Button
                    variant='outlined'
                    startIcon={<CreateNewFolderIcon />}
                    onClick={handleAddFolderClick}
                    aria-label={t('addFolder')}
                    disabled={isCreatingFolder}
                    sx={(theme) => ({
                      textTransform: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontWeight: 500,
                      [theme.breakpoints.down(401)]: {
                        minWidth: 'auto',
                        padding: '8px',
                        '& .MuiButton-icon': {
                          marginRight: '0px',
                          padding: '0px 10px 0px 12px',
                          width: 'auto !important',
                        },
                      },
                    })}
                  >
                    <Box
                      component='span'
                      sx={(theme) => ({
                        [theme.breakpoints.down(401)]: {
                          display: 'none',
                        },
                      })}
                    >
                      {t('addFolder')}
                    </Box>
                  </Button>
                )}

                {canUpload && (
                  <>
                    <input
                      type='file'
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant='contained'
                      startIcon={
                        <UploadIcon
                          sx={{
                            fill: theme.palette.primary.contrastText,
                          }}
                        />
                      }
                      onClick={handleOpenUploadModal}
                      disabled={isUploading}
                      aria-label={uploadLabel}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        padding: showIconOnlyUpload ? '8px' : '8px 16px',
                        fontWeight: 500,
                        ...(showIconOnlyUpload && {
                          minWidth: 'auto',
                          '& .MuiButton-icon': {
                            marginRight: '0px',
                            padding: '0px 10px 0px 12px',
                            width: 'auto',
                          },
                        }),
                      }}
                    >
                      {!showIconOnlyUpload && uploadLabel}
                    </Button>
                  </>
                )}
              </Stack>
            )}

            {showPersonalStorageNavigation && <PersonalStorageNavigation />}

            <Stack
              flex={1}
              sx={{ minHeight: 0 }}
            >
              {forceShowPlaceholder ? (
                <Stack
                  flex={1}
                  alignItems='center'
                  justifyContent='center'
                  sx={{ border: '1px dashed #ccc', borderRadius: 1, p: 4 }}
                >
                  <Typography color='text.secondary'>
                    {t('selectClientToViewDocumentVault')}
                  </Typography>
                </Stack>
              ) : (
                <PersonalStorageDropZone
                  enabled={canDropFiles}
                  onFilesDrop={handleFilesDropped}
                >
                  <React.Suspense fallback={<LoadingSpinner />}>
                    {SelectedTable &&
                      (() => {
                        const RenderedTable =
                          SelectedTable as unknown as React.ComponentType<any>;
                        return (
                          <RenderedTable
                            searchTerm={
                              selectedTab === 'overview'
                                ? liveSearchTerm
                                : searchTerm
                            }
                            wealthAccountId={wealthAccountId}
                            vaultFileCategoryId={vaultFileCategoryId}
                            folderId={currentFolderId}
                            isWidget={isWidget}
                            investorId={computedInvestorId}
                            {...(isAdvisor &&
                            wealthAccountId === undefined &&
                            (selectedTab === 'statements' ||
                              selectedTab === 'tax-documents')
                              ? {
                                  dateFilter: feedDateFilter,
                                  onDateFilterChange: setFeedDateFilter,
                                }
                              : {})}
                            paginated={isPaginatedVault}
                          />
                        );
                      })()}
                  </React.Suspense>
                </PersonalStorageDropZone>
              )}
            </Stack>
          </Stack>
        </Stack>
        <CreateFolderModal
          open={isCreateFolderModalOpen}
          onClose={handleModalClose}
          onCreate={handleModalCreate}
          isLoading={isCreatingFolder}
          error={createFolderError}
          onNameChange={() => {
            setCreateFolderError(undefined);
          }}
        />

        {/* Render the Upload Modal */}
        <UploadModal
          open={isUploadModalOpen}
          onClose={handleCloseUploadModal}
          onFilesSelect={handleFilesSelected}
          multiple={isAdvisorRole}
        />

        <EditDocumentModal
          open={isPendingUploadOpen}
          onClose={() => {
            setIsPendingUploadOpen(false);
          }}
          document={null}
          investorId={computedInvestorId}
          pendingUpload={pendingUpload}
        />

        {/* Activity Modal */}
        <DocumentsHistoryModal
          open={isHistoryOpen}
          onClose={() => {
            setIsHistoryOpen(false);
          }}
          investorId={computedInvestorId}
        />
      </VaultContext.Provider>
    </ContentContainer>
  );
};

const Header = ({
  rightSlot,
  hideFolderLevels = false,
  hideTitleWithoutClient,
}: {
  rightSlot?: React.ReactNode;
  hideFolderLevels?: boolean;
  hideTitleWithoutClient?: boolean;
}) => {
  const {
    client,
    /* headerTitle, */ folderPath,
    setFolderPath,
    wealthAccountId,
  } = useContext(VaultContext);
  const { currentRole } = useContext(UserContext);
  const { t } = useLocalization();

  const isAccountView = wealthAccountId !== undefined;

  const handleBreadcrumbClick = (index: number) => {
    if (setFolderPath) {
      // If index is -1, clear the path (navigate to root)
      // Otherwise, slice the path up to and including the clicked index
      const newPath = index === -1 ? [] : folderPath.slice(0, index + 1);
      setFolderPath(newPath);
    }
  };

  const rootLabel = React.useMemo(() => {
    if (isAccountView) {
      return t('Documents');
    } else if (currentRole.toLowerCase() === 'advisor' && client?.name) {
      return t('documentVault.clientVaultTitle', { name: client.name });
    } else if (hideTitleWithoutClient) {
      return undefined;
    } else {
      return t('documentVault.title');
    }
  }, [isAccountView, currentRole, client, hideTitleWithoutClient, t]);

  if (!rootLabel && !rightSlot) return null;

  return (
    <Stack
      direction='row'
      flexWrap='wrap'
      justifyContent='space-between'
      alignItems='center'
      gap={2}
    >
      {rootLabel && (
        <Breadcrumbs separator={<ChevronRightIcon />}>
          {/* Root Breadcrumb */}
          <Typography
            variant='h5'
            sx={{
              fontWeight: 600,
              cursor: 'pointer',
              overflowWrap: 'anywhere',
            }}
            onClick={() => {
              handleBreadcrumbClick(-1);
            }} // Index -1 for root
          >
            {rootLabel}
          </Typography>

          {/* Folder Path Breadcrumbs */}
          {(hideFolderLevels ? [] : folderPath).map((folder, index) => (
            <Stack
              direction='row'
              gap={1}
              key={folder.id} // Use folder.id as key
              alignItems='center'
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                handleBreadcrumbClick(index);
              }} // Pass the index
            >
              <FolderIcon />
              <Typography
                variant='h5'
                sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
                alignItems='center'
              >
                {folder.name} {/* Use folder.name for display */}
              </Typography>
            </Stack>
          ))}
        </Breadcrumbs>
      )}
      {rightSlot}
    </Stack>
  );
};

interface CreateFolderModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (folderName: string) => void;
  isLoading: boolean;
  error?: string;
  onNameChange: () => void;
}

const desktopPaperSx = {
  width: 400,
  border: '1px solid #ccc',
  borderRadius: 1,
  boxShadow: 24,
};

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  open,
  onClose,
  onCreate,
  isLoading,
  error,
  onNameChange,
}) => {
  const { t } = useLocalization();
  const isMobile = useMediaQuery(mobileWidth);
  const [folderName, setFolderName] = useState('');

  useEffect(() => {
    if (open) {
      setFolderName('');
    }
  }, [open]);

  const handleCreateClick = () => {
    if (folderName.trim()) {
      onCreate(folderName.trim());
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFolderName('');
      onClose();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(event.target.value);
    onNameChange();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && folderName.trim() && !isLoading) {
      handleCreateClick();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='create-folder-modal-title'
      fullScreen={isMobile}
      slotProps={{ paper: { sx: isMobile ? undefined : desktopPaperSx } }}
    >
      <Box sx={{ p: 4 }}>
        <Typography
          id='create-folder-modal-title'
          variant='h6'
          component='h2'
        >
          {t('addFolder')}
        </Typography>
        <TextField
          margin='normal'
          required
          fullWidth
          id='folderName'
          label={t('name')}
          name='folderName'
          autoComplete='off'
          autoFocus
          value={folderName}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          disabled={isLoading}
          error={Boolean(error)}
          helperText={error ?? ''}
        />
        <Stack
          direction='row'
          spacing={2}
          justifyContent='flex-end'
          mt={2}
        >
          <Button
            size='large'
            variant='outlined'
            onClick={handleClose}
            disabled={isLoading}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleCreateClick}
            size='large'
            variant='contained'
            disabled={!folderName.trim() || isLoading}
          >
            {isLoading ? t('saving') : t('save')}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default DocumentVault;
